import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, Box, TextField, Divider,
} from '@material-ui/core';

import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';

import useEventListener from '../../lib/useEventListener';
import RepairCard from '../metalCollection/RepairCardWithImage';
import RepairInformation from '../metalCollection/RepairInformation';
import SizingJobContainer from './SizingJobContainer';
import type { IScannedRepair, IRepairItem } from '../marking/MarkingInterfaces';

const Sizing: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [barcode, setBarcode] = React.useState<string>();

  const repairFetcher = (url: string, barcodeParam: string) => axios.get<IScannedRepair>(url, {
    params: {
      repairBarcode: barcodeParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      if (e.code === '404') {
        setBarcode(undefined);
      } else {
        setBarcode(undefined);
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const openRepair = debounce((barcodeInput: string) => {
    if (barcodeInput !== '') {
      setBarcode(barcodeInput);
    }
  }, 250);

  const { data } = useSWR(barcode ? ['/Sizing/GetRepair', barcode] : null, (url, barcodeParam) => repairFetcher(url, barcodeParam), {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    refreshWhenHidden: false,
    refreshInterval: 0,
  });

  const handleOutScan = (scannedBarcode: string | '') => {
    axios.post('/scanner/LogManualScan', {
      ScanType: 'Out',
      ScanBody: scannedBarcode,
      Location: 'Sizing Distribution',
    })
      .catch((er: AxiosError) => {
        enqueueSnackbar(er?.response?.data, { persist: false, variant: 'error' });
      });
  };

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.includes('In:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        openRepair(barcodeBuffer.replace('{*|}', ''));
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        handleOutScan(barcodeBuffer.replace('<>??<>', ''));
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  const RepairItemMap = (
    data?.repairItems?.map((repairItem:IRepairItem) => (
      <Grid item xs={12}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <RepairInformation RepairItem={repairItem} />
          </Grid>
        </Grid>
      </Grid>
    ))
  );

  if (!data) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in repair to review marking data, estimation data, and images of repair.'}</Alert>
        </Grid>
        <Grid item>
          <TextField
            variant="outlined"
            size="small"
            onKeyPress={
            (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                openRepair((event.target as HTMLInputElement).value);
              }
            }
            }
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <Grid container spacing={0}>
        <Grid container item spacing={1} sm={12} md={9}>
          <Grid item sm={12} md={1} />
          <Grid container item spacing={2} sm={12} md={11}>
            <Grid item xs={12}>
              <Alert
                severity="info"
              >
                {'Resize ring(s) to its new size listed below, then continue by scanning in another repair.'}
              </Alert>
            </Grid>
            <Grid item lg={6} xs={12}>
              <RepairCard
                barcode={barcode || ''}
                secondaryText={
                  `${data.repairItems?.length} item${data.repairItems?.length === 1 ? '' : 's'}`
                }
              />
            </Grid>
            <Grid item xs={12} />
            <>
              <Grid item xs={12}>
                <SizingJobContainer title="Resize Repair to:" repairJobs={data?.repairJobs} />
              </Grid>
              <Grid item xs={12}>
                <Box p={2} />
              </Grid>
            </>
            {RepairItemMap}
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Divider orientation="vertical" />
      </Grid>
    </Box>
  );
};

export default Sizing;
